import { Box } from '@material-ui/core';

import { SBase, SText } from '@setvi/shared/components';
import type { Resource } from '@setvi/shared/interfaces';

import type { PrintOrderItemCustomization } from '../../types';
import { PreviewPanel } from '../preview-panel';
import { LocationInput } from './components';
import { useLocationDialog } from './hooks/use-location-dialog';
import { useStyles } from './styles';

interface LocationDialogProps {
  resource: Resource;
  /** Existing customization when editing an item already in the cart. */
  initial?: PrintOrderItemCustomization;
  confirmLabel?: string;
  isEdit?: boolean;
  onClose: () => void;
}

/**
 * Location customization, opened on add-to-cart for a resource with
 * `HasLocation`. The rep generates the location PDF before the asset can be
 * added, so the preview always matches what was entered.
 */
export const LocationDialog = ({
  resource,
  initial,
  confirmLabel = 'Add to Cart',
  isEdit = false,
  onClose
}: LocationDialogProps) => {
  const classes = useStyles();
  const { input, preview, footer } = useLocationDialog({
    resource,
    initial,
    isEdit,
    onClose
  });

  return (
    <SBase
      width={960}
      header={{ title: resource.Name, onClose }}
      footer={{
        onConfirm: footer.handleConfirm,
        onClose,
        confirmLabel,
        closeLabel: 'Cancel',
        confirmLoading: footer.isSyncing,
        disableConfirm: !footer.canConfirm || footer.isSyncing,
        disableClose: footer.isSyncing,
        divider: true
      }}>
      <Box className={classes.content}>
        <Box className={classes.previewColumn}>
          <PreviewPanel
            document={preview.document}
            pages={preview.pages}
            hasPreview={preview.hasPreview}
            generating={preview.generating}
            emptyMessage="Showing the original asset. Generate location to preview it."
          />
        </Box>

        <Box className={classes.customizeColumn}>
          <SText title="Customize" size="lg" weight="bold" />
          <LocationInput {...input} />
        </Box>
      </Box>
    </SBase>
  );
};
